import { useEffect, useState } from 'react';
import { QueryClient } from 'react-query';

import { IdentifierDTO } from '@zazume/zzm-base';

import { CopilotChatKeys } from '#/hooks/api/copilot/Copilot.keys';
import { API } from '#/lib/network/API';

import { useCopilotChatIdStorage } from './useCopilotChatIdStorage';

type CopilotChat =
  | {
      chatId: IdentifierDTO;
      isLoading: false;
    }
  | {
      chatId: undefined;
      isLoading: true;
    };

const isValidConversation = async (chatId: IdentifierDTO): Promise<boolean> => {
  try {
    await new QueryClient().fetchQuery(CopilotChatKeys.getMessages(chatId), () => API.copilot.getConversation(chatId), {
      retry: false
    });
    return true;
  } catch (e) {
    return false;
  }
};

const extractChatId = async (
  chatIdParam: IdentifierDTO | undefined,
  chatIdLocalStorage: IdentifierDTO | null,
  addChatIdQueryParam: (value: string) => void,
  addChatIdLocalStorage: (value: string) => void
): Promise<IdentifierDTO> => {
  if (chatIdParam) {
    const isValid = await isValidConversation(chatIdParam);
    if (isValid) {
      addChatIdLocalStorage(chatIdParam);
      return chatIdParam;
    }
  }

  if (chatIdLocalStorage) {
    const isValid = await isValidConversation(chatIdLocalStorage);
    if (isValid) {
      addChatIdQueryParam(chatIdLocalStorage);
      return chatIdLocalStorage;
    }
  }

  const chatId = await API.copilot.createConversation();
  addChatIdLocalStorage(chatId);
  addChatIdQueryParam(chatId);
  return chatId;
};

export const useCopilotChat = (): CopilotChat => {
  const { addChatIdQueryParam, addChatIdLocalStorage, chatIdQueryParam, chatIdLocalStorage } =
    useCopilotChatIdStorage();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [chatId, setChatId] = useState<IdentifierDTO | undefined>(undefined);

  useEffect(() => {
    const setChat = async () => {
      const chatId = await extractChatId(
        chatIdQueryParam,
        chatIdLocalStorage,
        addChatIdQueryParam,
        addChatIdLocalStorage
      );
      setChatId(chatId);
      setIsLoading(false);
    };

    setChat();
  }, [chatIdQueryParam, chatIdLocalStorage, addChatIdQueryParam, addChatIdLocalStorage]);

  if (isLoading) {
    return {
      chatId: undefined,
      isLoading: true
    };
  }

  return {
    isLoading: false,
    chatId: chatId!
  };
};
